/**
 * Dynamic API Client
 * 
 * This module provides a dynamic API client that creates fresh clients
 * for each request, avoiding the static configuration issues. It properly
 * handles environment changes and provides a clean interface.
 */

import { createClient, createConfig } from '../generated/api/client'
import type { ClientOptions } from '../generated/api/types.gen'
import {
  userOperationsGetCurrentUser,
  userOperationsGetAvailableUsers,
  userOperationsSwitchCurrentUser,
  userOperationsUpdatePoints,
  userOperationsGetCustomersByIds,
  rewardOperationsGetRewards,
  rewardOperationsGetQuests,
  rewardOperationsUpdateReward,
  rewardOperationsRedeemReward,
  leaderboardOperationsGetLeaderboard,
  leaderboardOperationsGetFocusedLeaderboardData,
} from '../generated/api/sdk.gen'
import type {
  Customer,
  Reward,
  LeaderboardEntry,
  FocusedLeaderboard,
  CustomerId,
  RewardId,
} from '../generated/api/types.gen'

// API Error interface for better type safety
interface ApiError {
  message?: string
  code?: string
  details?: string
}

// API response interfaces for type safety
interface SwitchUserResponse {
  success: boolean
}

interface UpdatePointsResponse {
  success: boolean
}

interface RedeemRewardResponse {
  success: boolean
  customer?: Customer
  reward?: Reward
}

// Helper function to handle API responses
const handleApiResponse = <T>(response: { data?: T; error?: ApiError }): T => {
  if (response.error) {
    console.error('API Error:', response.error)
    throw new Error(response.error.message || 'API request failed')
  }
  if (!response.data) {
    throw new Error('No data received from API')
  }
  return response.data
}

// Helper to simulate API latency in development/demo mode
const simulateLatency = async (min = 300, max = 800): Promise<void> => {
  // Skip delay in test environment or production
  if (import.meta.env.CYPRESS || import.meta.env.PROD) return
  
  const delay = Math.floor(Math.random() * (max - min + 1)) + min
  await new Promise(resolve => setTimeout(resolve, delay))
}

/**
 * Create a fresh API client with current environment configuration
 */
const createFreshClient = (baseUrl?: string) => {
  // HARDCODED: Always use production backend
  const apiBaseUrl = baseUrl || 'https://backend.apex-cognition.com'
  
  return createClient(createConfig<ClientOptions>({
    baseUrl: apiBaseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  }))
}

/**
 * Dynamic API client that creates fresh clients for each request
 */
export const createDynamicApiClient = (baseUrl?: string) => {
  return {
    /**
     * User-related API methods
     */
    user: {
      async getCurrentUser(): Promise<Customer | null> {
        await simulateLatency()
        try {
          const client = createFreshClient(baseUrl)
          const response = await userOperationsGetCurrentUser({ client })
          return handleApiResponse(response)
        } catch (error) {
          // Return null if user not found, rather than throwing
          console.warn('No current user found:', error)
          return null
        }
      },
      
      async getAvailableUsers(): Promise<Customer[]> {
        await simulateLatency()
        const client = createFreshClient(baseUrl)
        const response = await userOperationsGetAvailableUsers({ client })
        return handleApiResponse(response)
      },
      
      async switchCurrentUser(customerId: string): Promise<boolean> {
        await simulateLatency()
        try {
          const client = createFreshClient(baseUrl)
          const response = await userOperationsSwitchCurrentUser({
            client,
            body: { customer_id: customerId as CustomerId }
          })
          const result = handleApiResponse<SwitchUserResponse>(response)
          return result.success
        } catch (error) {
          console.error('Failed to switch user:', error)
          return false
        }
      },
      
      async updatePoints(points: number): Promise<boolean> {
        await simulateLatency()
        try {
          const client = createFreshClient(baseUrl)
          const response = await userOperationsUpdatePoints({
            client,
            body: { points }
          })
          const result = handleApiResponse<UpdatePointsResponse>(response)
          return result.success
        } catch (error) {
          console.error('Failed to update points:', error)
          return false
        }
      },
      
      async getCustomersByIds(customerIds: string[]): Promise<Customer[]> {
        await simulateLatency()
        const client = createFreshClient(baseUrl)
        const response = await userOperationsGetCustomersByIds({
          client,
          body: customerIds as CustomerId[]
        })
        return handleApiResponse(response)
      }
    },
    
    /**
     * Rewards-related API methods
     */
    rewards: {
      async getRewards(): Promise<Reward[]> {
        await simulateLatency()
        const client = createFreshClient(baseUrl)
        const response = await rewardOperationsGetRewards({ client })
        return handleApiResponse(response)
      },
      
      async getQuests(): Promise<Reward[]> {
        await simulateLatency()
        const client = createFreshClient(baseUrl)
        const response = await rewardOperationsGetQuests({ client })
        return handleApiResponse(response)
      },
      
      async updateReward(reward: Reward): Promise<boolean> {
        await simulateLatency()
        try {
          const client = createFreshClient(baseUrl)
          const response = await rewardOperationsUpdateReward({
            client,
            path: { reward_id: reward.reward_id },
            body: reward
          })
          handleApiResponse(response)
          return true
        } catch (error) {
          console.error('Failed to update reward:', error)
          return false
        }
      },
      
      async redeemReward(rewardId: string, pointsToSpend: number): Promise<{ success: boolean; customer?: Customer; reward?: Reward }> {
        await simulateLatency()
        try {
          const client = createFreshClient(baseUrl)
          const response = await rewardOperationsRedeemReward({
            client,
            body: {
              reward_id: rewardId as RewardId,
              points_to_spend: pointsToSpend
            }
          })
          const result = handleApiResponse<RedeemRewardResponse>(response)
          return {
            success: result.success,
            customer: result.customer,
            reward: result.reward
          }
        } catch (error) {
          console.error('Failed to redeem reward:', error)
          return { success: false }
        }
      }
    },
    
    /**
     * Leaderboard-related API methods
     */
    leaderboard: {
      async getLeaderboard(): Promise<LeaderboardEntry[]> {
        await simulateLatency()
        const client = createFreshClient(baseUrl)
        const response = await leaderboardOperationsGetLeaderboard({ client })
        return handleApiResponse(response)
      },
      
      async getFocusedLeaderboardData(currentUserId: string): Promise<{ topThree: LeaderboardEntry[]; focusedView: LeaderboardEntry[] }> {
        await simulateLatency()
        const client = createFreshClient(baseUrl)
        const response = await leaderboardOperationsGetFocusedLeaderboardData({
          client,
          path: { customer_id: currentUserId as CustomerId }
        })
        const result: FocusedLeaderboard = handleApiResponse(response)
        return {
          topThree: result.top_three,
          focusedView: result.focused_view
        }
      }
    }
  }
}

/**
 * Default dynamic API client instance
 */
export const dynamicApiClient = createDynamicApiClient()